import { FC, useEffect } from "react";
import Typography from "@mui/material/Typography";
import { getSurveySection } from "app/features/surveySlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { FormControlModel } from "types";
import FormComponentFactory from "lib/factory/FormComponentFactory";
import { ElementType } from "lib/enums/enums";
import { questionIds } from "../../constants/initialValues";
import StepContent from "../StepContent";
import { YES, NO } from "pages/SurveyForm/constants/uiControls";

type Props = {
  formik: any;
  sectionId: number;
};

const Step11: FC<Props> = ({ formik, sectionId }) => {
  const dispatch = useAppDispatch();
  const { surveySection } = useAppSelector((state) => state.survey) ?? [];
  const { questions = [] } = surveySection || {};

  useEffect(() => {
    dispatch(getSurveySection(sectionId));
  }, []);

  const onBirthDateError = (name: string) => {
    formik.setFieldTouched(name, true, false);
  };

  const clearMateInfo = (value: string) => {
    if (value === NO) {
      formik.setFieldValue("mateDateOfBirth", { ...formik.values.mateDateOfBirth, value: "" });
      formik.setFieldValue("mateGender", { ...formik.values.mateGender, value: "" });
      formik.setFieldValue("areTogether", { ...formik.values.areTogether, value: "" });
      formik.setFieldValue("mateOccupation", { ...formik.values.mateOccupation, value: "" });
      formik.setFieldValue("isSameOccupation", { ...formik.values.isSameOccupation, value: "" });
      formik.setFieldValue("mateProfession", { ...formik.values.mateProfession, value: "" });
      formik.setFieldValue("educationLevel", { ...formik.values.educationLevel, value: "" });
      formik.setFieldValue("isCurrentlyStudying", { ...formik.values.isCurrentlyStudying, value: "" });
      formik.setFieldValue("isContributor", { ...formik.values.isContributor, value: "" });
      formik.setFieldValue("isDependant", { ...formik.values.isDependant, value: "" });
      formik.setFieldValue("hasDisability", { ...formik.values.hasDisability, value: "" });
      formik.setFieldValue("disability", { ...formik.values.disability, value: "" });
    }
  };

  const dataModel: Array<FormControlModel> = [
    {
      key: 0,
      formik,
      visible: true,
      name: "hasMate",
      questionData: questions[0],
      label: "¿Tiene pareja?",
      elementType: ElementType.Radio,
      onChangeCallback: clearMateInfo,
    },
    {
      key: 1,
      formik,
      name: "mateDateOfBirth",
      label: "Fecha de nacimiento",
      onBlurCallback: onBirthDateError,
      elementType: ElementType.DatePicker,
      visible: formik.values.hasMate?.value === YES,
      questionData: { id: questionIds.step11.mateDateOfBirth },
    },
    {
      key: 2,
      formik,
      label: "Género",
      name: "mateGender",
      elementType: ElementType.Select,
      questionData: questions[0]?.subQuestions[1],
      visible: formik.values.hasMate?.value === YES,
    },
    {
      key: 3,
      formik,
      name: "areTogether",
      label: "¿Vive con usted en el hogar?",
      elementType: ElementType.Radio,
      questionData: questions[0]?.subQuestions[2],
      visible: formik.values.hasMate?.value === YES,
    },
    {
      key: 4,
      formik,
      label: "Ocupación",
      name: "mateOccupation",
      elementType: ElementType.Select,
      questionData: questions[0]?.subQuestions[3],
      visible: formik.values.hasMate?.value === YES,
    },
    {
      key: 5,
      formik,
      name: "isSameOccupation",
      elementType: ElementType.Radio,
      label: "¿Trabaja en empresa de flores?",
      questionData: questions[0]?.subQuestions[4],
      visible: formik.values.hasMate?.value === YES,
    },
    {
      key: 6,
      formik,
      name: "mateProfession",
      label: "¿En qué sector trabaja?",
      elementType: ElementType.Select,
      questionData: questions[0]?.subQuestions[5],
      visible: formik.values.hasMate?.value === YES,
      checkboxStyle: {
        minWidth: "200px",
        margin: "0 1rem",
      },
    },
    {
      key: 7,
      formik,
      name: "educationLevel",
      elementType: ElementType.Select,
      label: "Último nivel educativo aprobado",
      questionData: questions[0]?.subQuestions[6],
      visible: formik.values.hasMate?.value === YES,
    },
    {
      key: 8,
      formik,
      name: "isCurrentlyStudying",
      label: "¿Está actualmente estudiando?",
      elementType: ElementType.Radio,
      questionData: questions[0]?.subQuestions[7],
      visible: formik.values.hasMate?.value === YES,
    },
    {
      key: 9,
      formik,
      name: "isContributor",
      elementType: ElementType.Radio,
      label: "¿Aporta económicamente al hogar?",
      questionData: questions[0]?.subQuestions[8],
      visible: formik.values.hasMate?.value === YES,
    },
    {
      formik,
      key: 10,
      name: "isDependant",
      elementType: ElementType.Radio,
      label: "¿Depende económicamente del trabajador?",
      questionData: questions[0]?.subQuestions[9],
      visible: formik.values.hasMate?.value === YES,
    },
    {
      key: 11,
      formik,
      name: "hasDisability",
      elementType: ElementType.Radio,
      label: "¿Presenta discapacidad?",
      questionData: questions[0]?.subQuestions[10],
      visible: formik.values.hasMate?.value === YES,
    },
    {
      formik,
      key: 12,
      maxLength: 100,
      label: "¿Cual?",
      name: "disability",
      elementType: ElementType.Textfield,
      questionData: questions[0]?.subQuestions[11],
      visible: formik.values.hasMate?.value === YES && formik.values.hasDisability?.value === YES,
    },
  ];
  return (
    <div>
      <Typography mb={2} variant='subtitle1' align='center'>
        Cuéntenos sobre su familia: Datos del cónyuge/ pareja
      </Typography>
      <StepContent>{dataModel.map(FormComponentFactory)}</StepContent>
    </div>
  );
};

export default Step11;
